import * as d3 from 'd3';

import { LoggerFactory, ILogger } from '../../helpers/helpers';
import { IAreaConfiguration } from '../interfaces/IAreaConfiguration';
import { whiteBackgroundColor } from '../../../common/constants';

export class StaticArea {
    private _group: d3.Selection<any>;
    private _scale: d3.time.Scale<any, any>;
    private _areas: IAreaConfiguration[];
    private _logger: ILogger;
    private _height: number;
    private _width: number;
    private _format = d3.time.format('%Y-%m-%d %H:%M');
    private _clipPathId: string;
    private _dateLabelWidth = 40;
    private _onClick: (d: IAreaConfiguration) => void;
    private _onBrush: (d: any) => void;
    private _onBrushEnd: (d: [any, any]) => void;

    private _brushGroup: d3.Selection<any>;
    private _brush: d3.svg.Brush<any, any, any>;
    private _editedArea: IAreaConfiguration;

    constructor(container: d3.Selection<any>, scale: d3.time.Scale<any, any>, clipPathId: string, width: number, height: number) {
        this._height = height;
        this._width = width;
        this._scale = scale;
        this._clipPathId = clipPathId;
        this._areas = [];
        this._logger = LoggerFactory.getLogger('WideTech.Charting.staticArea');

        this.init(container);
    }

    private init(container) {
        this._group = container.append('g')
            .classed('areas', true);

        this._brushGroup = container.append('g')
            .classed('brush', true);

        this._brush = d3.svg.brush()
            .x(<any>this._scale)
            .on('brush', () => {
                if (this._onBrush) {
                    const extent = this._brush.extent();
                    this._onBrush(extent);
                }
            })
            .on('brushend', () => {
                if (this._onBrushEnd) {
                    const extent = this._brush.extent();
                    this._onBrushEnd(extent);
                }
            });

    }

    private append(enterSelection: d3.selection.Enter<IAreaConfiguration>) {
        try {
            this._logger.debug('Creating new areas.');
            const groups = enterSelection
                .append('g')
                .classed('area', true)
                .on('mouseenter', () => {
                    d3.select((<any>d3.event).currentTarget)
                        .selectAll('text:not(.area-icon-content)')
                        .style('visibility', (d) => d.isHidingDatesOnHover ? 'hidden' : 'visible');
                })
                .on('mouseout', () => {
                    d3.select((<any>d3.event).currentTarget)
                        .selectAll('text:not(.area-icon-content)')
                        .style({ 'visibility': 'hidden' });
                });
            groups
                .append('rect')
                .classed('area-display', true)
                .attr({
                    'x': 0,
                    'y': 0,
                    'width': this.areaWidth(),
                    'height': this.height()
                })
                .on('click', (d) => {
                    if (this._onClick) {
                        this._onClick(d);
                    }
                });

            groups.append('line')
                .classed('area-begin', true)
                .classed('area-bound', true)
                .style({ 'visibility': 'hidden' })
                .attr({
                    'x1': 0, 'y1': 0,
                    'x2': 0, 'y2': this.height()
                });

            groups.append('line')
                .classed('area-end', true)
                .classed('area-bound', true)
                .style({ 'visibility': 'hidden' })
                .attr({
                    'x1': this.areaWidth(), 'y1': 0,
                    'x2': this.areaWidth(), 'y2': this.height()
                });
            groups.append('title');

            const iconGroup = groups.append('g')
                .classed('area-icon-group', true);

            iconGroup.append('circle')
                .classed('area-icon', true)
                .style({
                    'visibility': 'hidden',
                    'stroke': 'transparent',
                    'stroke-width': '10px',
                    'text-anchor': 'middle'
                })
                .attr({ 'cx': 0, 'cy': 0, 'r': 0 });

            iconGroup.append('title');

            iconGroup.append('text')
                .classed('area-icon-content', true)
                .style({
                    'fill': whiteBackgroundColor,
                    'text-anchor': 'middle'
                }).attr({ 'x': '0', 'y': '0' });

            groups.append('text').classed('label', true);
            groups.append('text')
                .classed({
                    'beginDate': true,
                    'date-label': true
                })
                .attr({
                    'x': -this._dateLabelWidth
                });
            groups.append('text')
                .classed({
                    'endDate': true,
                    'date-label': true
                })
                .attr({
                    'x': this.endDatePosition()
                });
            groups.selectAll('text:not(.area-icon-content)')
                .style({ 'visibility': 'hidden' });
            this._logger.info('Areas created.');
        } catch (e) {
            this._logger.error(e);
        }
    }

    private update(selection: d3.selection.Update<IAreaConfiguration>): void {
        selection
            //.transition()
            .attr({
                'transform': d => `translate(${this.calcMin(d)} , 0)`,
                'data-myId': d => `area${d.name}`
            });
        selection.select('.area-display')
            .style({
                'fill': d => d.color,
                'opacity': d => (d.opacity || d.opacity === 0) ? d.opacity : 1
            })
            .attr({
                'width': this.areaWidth()
            });

        //.area-bound
        selection.select('.area-begin')
            .style({
                'stroke': d => d.color,
                'stroke-dasharray': 5,
                'visibility': d => {
                    return d.showBoundaries ? 'visible' : 'hidden';
                }
            });

        selection.select('.area-end')
            .style({
                'stroke': d => d.color,
                'stroke-dasharray': 5,
                'visibility': d => {
                    return d.showBoundaries ? 'visible' : 'hidden';
                }
            });

        const iconGroup = selection
            .select('.area-icon-group')
            .attr('transform', d => {
                const size = Math.floor(d.iconSize / 2);
                return `translate(0, -${isNaN(size) ? 0 : size})`;
            })
            .classed('clickable', d => !!d.onClick)
            .on('click', (d, i) => {
                if (d.onClick) {
                    d.onClick(d);
                }
            });

        iconGroup.select('.area-icon')
            .attr({
                'r': d => d.iconSize,
                'fill': d => d.color,
            })
            .style({ 'visibility': d => d.showBoundaries ? 'visible' : 'hidden' });

        iconGroup
            .select('title')
            .html(d => d.iconTitle);

        iconGroup.select('.area-icon-content')
            .attr({ 'y': d => (isNaN(d.iconSize) ? 0 : d.iconSize) / 2 })
            .text(d => d.iconContent);

        selection.select('.label')
            .text(d => d.name);

        selection.select('title')
            .text(d => `${d.name} (${this._format(d.beginDate)} - ${this._format(d.endDate)})`);

        selection.select('.beginDate')
            .text((d: any) => this._format(d.beginDate));

        selection.select('.endDate')
            .attr({ 'x': this.endDatePosition() })
            .text((d: any) => this._format(d.endDate));
        //this.updateScale(selection);
        this.updateSize(selection);
    }

    private calcMin(d: IAreaConfiguration): number {
        let begin = this._scale(d.beginDate);
        if (begin < 0) {
            begin = 0;
        }
        return begin;
    }

    private calcMax(d: IAreaConfiguration): number {
        const begin = this.calcMin(d);
        let end = this._scale(d.endDate);
        if (end > this.width()) {
            end = this.width();
        }
        const width = end - begin;
        return width;
    }

    private updateScale(selection) {
        this._logger.info('Updating data display according to scale.');
        selection
            //.transition()
            .attr({
                'transform': d => `translate(${this.calcMin(d)} , 0)`
            });
        selection.select('.endDate')
            .attr({
                'x': this.endDatePosition()
            });
        selection.select('.area-display')
            //.transition()
            .attr({
                'width': this.areaWidth()
            });
        selection.selectAll('.area-bound')
            .attr({ 'y2': this.height() });
        selection.select('.area-end')
            .attr({
                'x1': this.areaWidth(),
                'x2': this.areaWidth()
            });

        this._logger.info('Data display updated.');
    }

    private updateSize(selection) {
        selection.selectAll('.label')
            .attr({ 'y': this.height() / 2 });
        selection.selectAll('rect')
            .attr({
                'height': this.height()
            });
        selection.selectAll('.date-label')
            .attr({
                'y': this.height() + 10
            });
    }

    private areaWidth(): (d: any, i: number) => number {
        return (d, i) => {
            let width = this.calcMax(d);
            if (width < 0 || isNaN(width)) {
                this._logger.warning('Width is negative');
                width = 0;
            } else if (width < 1) {
                width = 1;
            }
            return width;
        };
    }

    private endDatePosition(): (d: IAreaConfiguration, i: number) => number {
        return (d, i) => {
            return this.calcMax(d) - this._dateLabelWidth;
        };
    }

    updateAreas(areas: IAreaConfiguration[]) {
        try {
            if (areas) {
                this._areas = areas;
                this._logger.debug('Drawing areas.');

                const selection = this._group
                    .selectAll('.area')
                    .data(areas);

                selection.exit().remove();
                this.append(selection.enter());

                this.update(selection);

                this._logger.debug('Areas drawn.');
            }

        } catch (e) {
            this._logger.error(e);
        }
    }

    scale(value?: d3.time.Scale<any, any>) {
        if (value) {
            const domain = value.domain();
            this._logger.debug(`Updating scale: ${domain[0]}-${domain[1]}`);
            this._scale = value;
            this.updateScale(this._group.selectAll('.area'));
            this._brush.x(<any>this._scale);
            this._brushGroup.call(this._brush);
        }
    }

    resize(width: number, height: number) {
        this.height(height);
        this.width(width);
        this.updateSize(this._group.selectAll('.area'));

        this._brush.x(<any>this._scale);
        this.resizeBrushGroup(this.height());
    }

    resizeBrushGroup(height: number) {
        this._brushGroup
            .call(this._brush)
            .selectAll('rect')
            .attr({
                height: height
            });
        this._brushGroup.select('.background')
            .attr('height', 0);
    }

    clickCallback(value?: (d: IAreaConfiguration) => void) {
        if (value) {
            this._onClick = value;
        }
    }

    brushCallback(value?: (d: [Date, Date]) => void) {
        if (value) {
            this._onBrush = value;
        }
    }

    brushEndCalback(value?: (d: [Date, Date]) => void) {
        if (value) {
            this._onBrushEnd = value;
        }
    }

    editedArea(value: IAreaConfiguration) {
        if (value) {
            this._editedArea = value;
            this._brush.extent(<any>[value.beginDate, value.endDate]);
            this._brushGroup.style('display', 'block');
            this._brushGroup.call(this._brush)
                .select('.extent')
                .attr('fill', value.color);
            this.resizeBrushGroup(this.height());
        } else {
            this.resizeBrushGroup(0);
            this._brushGroup.style('display', 'none');
        }
    }

    private height(value?: number) {
        if (value) {
            this._height = isNaN(value) ? 0 : value;
        }
        return this._height;
    }

    private width(value?: number) {
        if (value) {
            this._width = isNaN(value) ? 0 : value;
        }
        return this._width;
    }

}